import React, { useCallback, useEffect, useMemo, useState } from "react";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import { icons } from "../../../constants";
import {
  callActionApiAsync,
  callApiAsync,
  getBiomedicalEquipmentModelAdverseEvents,
  postDeleteBiomedicalEquipmentModelAdverseEvent
} from "../../../services/apiService";
import ModelAdverseEventsModal from "./ModelAdverseEventsModal";

const adverseEventColumns = [
  { field: "typeEventName", header: "Tipo de Suceso" },
  { field: "gravityLevelName", header: "Gravedad" },
  {
    field: "actionButtons",
    header: "",
    columnType: "actionButtons"
  }
];

const ModelAdverseEventsTab = ({
  modelRow,
  userId,
  isCatalogAdmin,
  pushToast,
  typeEventOptions,
  gravityOptions
}) => {
  const [eventRows, setEventRows] = useState([]);
  const [openEventModal, setOpenEventModal] = useState(false);

  const selectedModelId = modelRow?.value ? Number(modelRow.value) : null;

  const loadModelAdverseEvents = useCallback(() => {
    if (!selectedModelId) {
      setEventRows([]);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelAdverseEvents(selectedModelId, userId),
      (data) => {
        setEventRows(data?.table || []);
      }
    );
  }, [selectedModelId, userId]);

  useEffect(() => {
    if (!selectedModelId) {
      setEventRows([]);
      return;
    }

    loadModelAdverseEvents();
  }, [loadModelAdverseEvents, selectedModelId]);

  const handleDeleteEvent = useCallback(
    async (row) => {
      if (!row?.id || !userId) return;
      if (
        !window.confirm(
          `¿Eliminar la combinación "${row.typeEventName} / ${row.gravityLevelName}" del modelo seleccionado?`
        )
      ) {
        return;
      }

      await callActionApiAsync(
        () =>
          postDeleteBiomedicalEquipmentModelAdverseEvent({
            id: row.id,
            userId
          }),
        () => {
          loadModelAdverseEvents();
          pushToast(
            "success",
            "Eventos adversos por modelo",
            "La combinación se eliminó de la plantilla del modelo."
          );
        },
        (error) => {
          pushToast(
            "error",
            "Eventos adversos por modelo",
            typeof error === "string" && error
              ? error
              : "No se pudo eliminar la combinación del modelo."
          );
        }
      );
    },
    [loadModelAdverseEvents, pushToast, userId]
  );

  const tableButtons = useMemo(
    () =>
      isCatalogAdmin
        ? [
            {
              type: "icon",
              icon: icons.redTrash,
              onClick: handleDeleteEvent
            }
          ]
        : [],
    [handleDeleteEvent, isCatalogAdmin]
  );

  if (!selectedModelId) {
    return (
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-value admin-catalog-hint">
          Seleccione una marca y un modelo para administrar los eventos adversos
          por defecto.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-label">Modelo seleccionado</p>
        <p className="admin-catalog-detail-value">{modelRow?.name || "—"}</p>
        <p className="admin-catalog-hint">
          Las combinaciones configuradas aquí se agregarán automáticamente al
          equipo cuando falten. Los eventos ya existentes en equipos previos no se
          borrarán ni se modificarán.
        </p>
      </div>

      <div className="mt-25">
        <div className="IHContainer j-space-between mb-15">
          <p className="createRist-title ml-0">
            Eventos adversos
          </p>
          {isCatalogAdmin && (
            <LaddaButton
              width={"200px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              margin={"0"}
              onClick={() => setOpenEventModal(true)}
            >
              <span className="HContainer align-center">
                <img className="btn-icon mr-10" src={icons.plusIcon} alt="icon" />
                Nuevo Evento Adverso
              </span>
            </LaddaButton>
          )}
        </div>

        <SimpleTable
          data={eventRows}
          columns={adverseEventColumns}
          scrollHeight={"280px"}
          text={"Eventos adversos del modelo"}
          buttons={tableButtons}
          emptyMessage="No hay combinaciones configuradas para este modelo."
        />
      </div>

      <ModelAdverseEventsModal
        open={openEventModal}
        close={() => setOpenEventModal(false)}
        modelId={selectedModelId}
        userId={userId}
        typeEventOptions={typeEventOptions}
        gravityOptions={gravityOptions}
        onSaved={() => {
          loadModelAdverseEvents();
          pushToast(
            "success",
            "Eventos adversos por modelo",
            "La combinación se agregó correctamente a la plantilla del modelo."
          );
        }}
        isViewer={!isCatalogAdmin}
      />
    </>
  );
};

export default ModelAdverseEventsTab;
